import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getActorById, getDramasByActor } from "@/lib/content";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ActorDetail() {
  const { id } = useParams();
  const actor = getActorById(id);

  useSeo({
    title: actor
      ? `风流女管家 - ${actor.name}演员资料及参演作品`
      : "风流女管家 - 演员资料",
    description: actor
      ? `${actor.name}演员资料：出生于${actor.birthplace}，代表作包括${actor.signatureRoles.join(
          "、"
        )}等。查看参演剧集与角色介绍。`
      : "风流女管家演员资料页，查看演员个人简介与参演作品。",
    keywords: actor
      ? `${actor.name},${actor.name}资料,${actor.name}作品,风流女管家,演员表`
      : "风流女管家,演员资料,演员表",
  });

  if (!actor) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-user-unfollow-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">
          暂无该演员详细信息
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          这位演员的资料还在整理中，先看看其他演员吧。
        </p>
        <Link
          to="/category?tab=actor"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回演员列表
        </Link>
      </div>
    );
  }

  const works = getDramasByActor(actor.id);

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "演员介绍", to: "/category?tab=actor" },
              { label: actor.name },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
        {/* Profile header */}
        <section className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <span className="block h-72 w-56 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:h-80 sm:w-64">
            <img
              src={actor.avatar}
              alt={`${actor.name}头像`}
              title={`${actor.name} 演员头像`}
              className="h-full w-full object-cover object-top"
            />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {actor.name}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{actor.englishName}</p>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 border-y border-background-200 py-5 text-sm sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">生日：</dt>
                <dd className="text-foreground-900">{actor.birthday}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">出生地：</dt>
                <dd className="text-foreground-900">{actor.birthplace}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">身高：</dt>
                <dd className="text-foreground-900">{actor.height}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">代表角色：</dt>
                <dd className="text-foreground-900">
                  {actor.signatureRoles.join("、")}
                </dd>
              </div>
            </dl>

            <div className="mt-5">
              <h2 className="mb-2 text-base font-semibold text-foreground-900">
                角色简介
              </h2>
              <p className="text-sm leading-7 text-foreground-700">
                {actor.intro}
              </p>
            </div>
          </div>
        </section>

        {/* Works */}
        <section className="mt-14">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-film-line" />
            </span>
            参演剧集
          </h2>
          {works.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {works.map((drama) => (
                <Link
                  key={drama.id}
                  to={`/detail/${drama.id}`}
                  className="group cursor-pointer"
                >
                  <span className="relative block aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
                    <img
                      src={drama.poster}
                      alt={`${drama.title}海报`}
                      title={`${drama.title} 剧集海报`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="mt-2 block truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {drama.title}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-primary-600">
                    饰 {actor.roleInDrama[drama.id] || actor.signatureRoles[0]}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
              <p className="text-sm text-foreground-500">暂无该演员的参演记录</p>
            </div>
          )}
        </section>

        {/* News */}
        <section className="mt-14">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-newspaper-line" />
            </span>
            相关资讯与花絮
          </h2>
          {actor.news.length > 0 ? (
            <ul className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {actor.news.map((item) => (
                <li
                  key={item.title}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-4 transition-colors hover:bg-background-100"
                >
                  <span className="flex items-center gap-2 text-sm text-foreground-800">
                    <i className="ri-article-line text-primary-600" />
                    {item.title}
                  </span>
                  <span className="text-xs text-foreground-400">
                    {item.date}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
              <p className="text-sm text-foreground-500">暂无相关资讯</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}